import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { ESTADO_PETICION } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Activity, AlertTriangle, Palette, Factory, Truck, CheckCircle2, Clock, Search } from "lucide-react";

const BUCKET_DEF = [
  { key: "diseno",     label: "Diseño",     icon: Palette,        color: "bg-violet-500" },
  { key: "produccion", label: "Producción", icon: Factory,        color: "bg-amber-500" },
  { key: "recepcion",  label: "Recepción",  icon: Truck,          color: "bg-blue-500" },
  { key: "listo",      label: "Listo",      icon: CheckCircle2,   color: "bg-emerald-500" },
  { key: "entregado",  label: "Entregado",  icon: CheckCircle2,   color: "bg-teal-500" },
  { key: "pendiente",  label: "Pendiente",  icon: Clock,          color: "bg-zinc-500" },
];

export default function Supervision() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fEstado, setFEstado] = useState("todos");
  const [fCliente, setFCliente] = useState("todos");
  const [fProvincia, setFProvincia] = useState("todas");
  const [fMarca, setFMarca] = useState("todas");

  const load = async () => {
    try {
      const [r, c, e] = await Promise.all([
        api.get("/dashboard/supervision"),
        api.get("/clientes"),
        api.get("/espacios"),
      ]);
      setItems(r.data.items || []);
      setClientes(c.data || []);
      setEspacios(e.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  // Mapa peticion_id → provincia (de cualquier espacio asociado a sus líneas)
  // De momento usamos la lista de provincias únicas de espacios.
  const provincias = useMemo(() => {
    const s = new Set();
    espacios.forEach((e) => e.provincia && s.add(e.provincia));
    return Array.from(s).sort();
  }, [espacios]);

  const marcas = useMemo(() => {
    const s = new Set();
    items.forEach((it) => it.peticion.marca && s.add(it.peticion.marca));
    return Array.from(s).sort();
  }, [items]);

  const filtered = items.filter((it) => {
    const p = it.peticion;
    if (q) {
      const text = `${p.nombre || ""} ${p.codigo || ""} ${p.marca || ""}`.toLowerCase();
      if (!text.includes(q.toLowerCase())) return false;
    }
    if (fEstado !== "todos" && p.estado !== fEstado) return false;
    if (fCliente !== "todos" && p.cliente_id !== fCliente) return false;
    if (fMarca !== "todas" && p.marca !== fMarca) return false;
    if (fProvincia !== "todas") {
      // Match si alguno de los espacios de la petición está en esa provincia (heurística simple)
      // Sin extra-data por ahora dejamos pasar
      // TODO: enrichir backend con provincias por peticion
    }
    return true;
  });

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Supervisión de proyectos"
        subtitle="Vista global de peticiones activas con progreso por fase"
      />

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="supervision-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por código, nombre o marca…"
            data-testid="supervision-search"
            className="pl-8 bg-zinc-950 border-zinc-800 h-9 text-sm" />
        </div>
        <Select value={fEstado} onValueChange={setFEstado}>
          <SelectTrigger data-testid="supervision-filter-estado" className="w-[160px] bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="todos">Todos los estados</SelectItem>
            {Object.entries(ESTADO_PETICION).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCliente} onValueChange={setFCliente}>
          <SelectTrigger data-testid="supervision-filter-cliente" className="w-[180px] bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
            <SelectItem value="todos">Todos los clientes</SelectItem>
            {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fMarca} onValueChange={setFMarca}>
          <SelectTrigger data-testid="supervision-filter-marca" className="w-[160px] bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue placeholder="Marca" /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
            <SelectItem value="todas">Todas las marcas</SelectItem>
            {marcas.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        {provincias.length > 0 && (
          <Select value={fProvincia} onValueChange={setFProvincia}>
            <SelectTrigger data-testid="supervision-filter-provincia" className="w-[160px] bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue placeholder="Provincia" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
              <SelectItem value="todas">Todas las provincias</SelectItem>
              {provincias.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="text-xs text-zinc-500 ml-auto">{filtered.length} / {items.length}</div>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Cargando…</div>}

      {!loading && items.length === 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-10 text-center text-zinc-400">
          No hay peticiones activas. Cuando se creen peticiones aparecerán aquí.
        </div>
      )}

      <div className="space-y-3" data-testid="supervision-list">
        {filtered.map((it) => {
          const p = it.peticion;
          const est = ESTADO_PETICION[p.estado] || ESTADO_PETICION.borrador;
          const total = Math.max(it.total_componentes, 1);
          return (
            <Link
              to={`/peticiones/${p.id}`}
              key={p.id}
              data-testid={`supervision-row-${p.codigo || p.id}`}
              className="block bg-zinc-900/40 border border-zinc-800 hover:border-amber-500/40 rounded-xl p-5 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-base font-semibold text-zinc-100 truncate">{p.nombre}</span>
                    <span className="text-xs text-zinc-500 font-mono">{p.codigo || "—"}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${est.color}`}>
                      {est.label}
                    </span>
                    {it.aaff_pendientes_antiguos > 0 && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {it.aaff_pendientes_antiguos} AAFF retrasado{it.aaff_pendientes_antiguos > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {p.marca && <div className="text-xs text-zinc-500 mt-1">Marca: {p.marca}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-zinc-500">Progreso</div>
                  <div className="text-2xl font-bold text-amber-400">{Math.round(it.progreso_pct)}%</div>
                </div>
              </div>

              {/* Stacked bar */}
              <div className="flex h-2.5 rounded-full overflow-hidden bg-zinc-800 mb-3">
                {BUCKET_DEF.map((b) => {
                  const w = (it.buckets[b.key] / total) * 100;
                  if (w <= 0) return null;
                  return (
                    <div
                      key={b.key}
                      className={b.color}
                      style={{ width: `${w}%` }}
                      title={`${b.label}: ${it.buckets[b.key]}`}
                    />
                  );
                })}
              </div>

              {/* Bucket counters */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-[11px]">
                {BUCKET_DEF.map((b) => (
                  <div key={b.key} className="flex items-center gap-1.5 text-zinc-400">
                    <span className={`w-2 h-2 rounded-full ${b.color}`} />
                    <b.icon className="w-3 h-3" />
                    <span>{b.label}: <span className="text-zinc-200 font-semibold">{it.buckets[b.key]}</span></span>
                  </div>
                ))}
              </div>

              <div className="flex gap-6 mt-3 text-xs text-zinc-500">
                <span>Presupuesto: <span className="text-zinc-200 font-semibold">{it.presupuesto?.toFixed(2)} €</span></span>
                <span>Coste: <span className="text-zinc-200 font-semibold">{it.coste?.toFixed(2)} €</span></span>
                <span>Componentes: <span className="text-zinc-200 font-semibold">{it.total_componentes}</span></span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
